export const toDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value?.toDate === 'function') return value.toDate();
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getDateRange = (preset = 'month', startDate = '', endDate = '') => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  let start;
  let end = today;

  if (preset === 'custom') {
    const customStart = toDate(startDate);
    const customEnd = toDate(endDate);
    start = customStart || new Date(today.getFullYear(), today.getMonth(), 1);
    end = customEnd ? new Date(customEnd.getFullYear(), customEnd.getMonth(), customEnd.getDate(), 23, 59, 59, 999) : today;
    return { start, end };
  }

  if (preset === 'week') {
    const dayOfWeek = today.getDay();
    start = new Date(today);
    start.setDate(today.getDate() - dayOfWeek);
    start.setHours(0, 0, 0, 0);
    return { start, end };
  }

  if (preset === 'quarter') {
    const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
    start = new Date(today.getFullYear(), quarterStartMonth, 1);
    return { start, end };
  }

  if (preset === 'year') {
    start = new Date(today.getFullYear(), 0, 1);
    return { start, end };
  }

  start = new Date(today.getFullYear(), today.getMonth(), 1);
  return { start, end };
};

const sum = (values) => values.reduce((acc, value) => acc + value, 0);

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const round2 = (value) => Math.round(value * 100) / 100;

export const percentChange = (current, previous) => {
  if (!previous) return current === 0 ? 0 : 100;
  return ((current - previous) / Math.abs(previous)) * 100;
};

const getTransactionDate = (transaction) => (
  toDate(transaction.date) ||
  toDate(transaction.issueDate) ||
  toDate(transaction.createdAt)
);

const inRange = (date, start, end) => {
  if (!date) return false;
  return date >= start && date <= end;
};

const getPeriodSimpleTotals = (transactions, start, end) => {
  const simpleTransactions = transactions.filter((tx) => tx.type === 'simple' && inRange(getTransactionDate(tx), start, end));
  const income = sum(simpleTransactions.map((tx) => Math.max(toNumber(tx.amount), 0)));
  const expenses = sum(simpleTransactions.map((tx) => Math.abs(Math.min(toNumber(tx.amount), 0))));
  return {
    income: round2(income),
    expenses: round2(expenses),
    netProfit: round2(income - expenses),
    cashIn: round2(income),
    cashOut: round2(expenses)
  };
};

const bucketByCategory = (transactions, direction) => {
  const buckets = {};
  transactions.forEach((tx) => {
    const amount = toNumber(tx.amount);
    const include = direction === 'income' ? amount > 0 : amount < 0;
    if (!include) return;

    const category = tx.category || 'Uncategorized';
    const normalized = direction === 'income' ? amount : Math.abs(amount);
    buckets[category] = (buckets[category] || 0) + normalized;
  });

  return Object.entries(buckets)
    .map(([category, amount]) => ({ category, amount: round2(amount) }))
    .sort((a, b) => b.amount - a.amount);
};

export const buildFinanceModel = ({ transactions, accounts, rangeStart, rangeEnd, now = new Date() }) => {
  const allTransactions = Array.isArray(transactions) ? transactions : [];
  const allAccounts = Array.isArray(accounts) ? accounts : [];

  const periodTransactions = allTransactions.filter((tx) => inRange(getTransactionDate(tx), rangeStart, rangeEnd));
  const periodSimpleTransactions = periodTransactions.filter((tx) => tx.type === 'simple');
  const periodInvoices = periodTransactions.filter((tx) => tx.type === 'invoice');
  const periodBills = periodTransactions.filter((tx) => tx.type === 'bill');

  const current = getPeriodSimpleTotals(allTransactions, rangeStart, rangeEnd);

  const rangeDuration = Math.max(1, Math.ceil((rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)));
  const previousEnd = new Date(rangeStart);
  previousEnd.setMilliseconds(-1);
  const previousStart = new Date(previousEnd);
  previousStart.setDate(previousEnd.getDate() - rangeDuration);
  previousStart.setHours(0, 0, 0, 0);
  const previous = getPeriodSimpleTotals(allTransactions, previousStart, previousEnd);

  const openInvoices = allTransactions.filter((tx) => tx.type === 'invoice' && toNumber(tx.balanceDue) > 0);
  const openBills = allTransactions.filter((tx) => tx.type === 'bill' && toNumber(tx.balanceDue) > 0);

  const receivablesOutstanding = round2(sum(openInvoices.map((tx) => toNumber(tx.balanceDue))));
  const payablesOutstanding = round2(sum(openBills.map((tx) => toNumber(tx.balanceDue))));

  const overdueReceivablesList = openInvoices.filter((tx) => {
    const due = toDate(tx.dueDate);
    return due && due < now;
  });
  const overduePayablesList = openBills.filter((tx) => {
    const due = toDate(tx.dueDate);
    return due && due < now;
  });

  const overdueReceivables = round2(sum(overdueReceivablesList.map((tx) => toNumber(tx.balanceDue))));
  const overduePayables = round2(sum(overduePayablesList.map((tx) => toNumber(tx.balanceDue))));

  const taxCollected = round2(sum(periodInvoices.map((tx) => toNumber(tx.taxAmount))));
  const taxPaid = round2(sum(periodBills.map((tx) => toNumber(tx.taxAmount))));

  const cashBalance = round2(sum(
    allAccounts.map((account) => {
      const value = account.currentBalance ?? account.balance ?? account.openingBalance;
      return toNumber(value);
    })
  ));

  const balanceSheet = {
    assets: round2(cashBalance + receivablesOutstanding),
    liabilities: payablesOutstanding
  };
  balanceSheet.equity = round2(balanceSheet.assets - balanceSheet.liabilities);

  const recentTransactions = [...allTransactions]
    .sort((a, b) => (getTransactionDate(b)?.getTime() || 0) - (getTransactionDate(a)?.getTime() || 0))
    .slice(0, 8)
    .map((tx) => ({ ...tx, _date: getTransactionDate(tx) }));

  const revenueByCategory = bucketByCategory(periodSimpleTransactions, 'income');
  const expensesByCategory = bucketByCategory(periodSimpleTransactions, 'expense');

  const monthlyNet = Array.from({ length: 6 }).map((_, index) => {
    const cursor = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1, 0, 0, 0, 0);
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59, 999);
    const totals = getPeriodSimpleTotals(allTransactions, monthStart, monthEnd);
    return {
      label: monthStart.toLocaleDateString('en-US', { month: 'short' }),
      net: totals.netProfit,
      income: totals.income,
      expenses: totals.expenses
    };
  });

  return {
    current,
    previous,
    changes: {
      income: round2(percentChange(current.income, previous.income)),
      expenses: round2(percentChange(current.expenses, previous.expenses)),
      netProfit: round2(percentChange(current.netProfit, previous.netProfit))
    },
    counts: {
      transactionsInPeriod: periodTransactions.length,
      invoicesInPeriod: periodInvoices.length,
      billsInPeriod: periodBills.length
    },
    receivables: {
      outstanding: receivablesOutstanding,
      overdue: overdueReceivables,
      openCount: openInvoices.length,
      overdueCount: overdueReceivablesList.length,
      top: overdueReceivablesList
        .sort((a, b) => toNumber(b.balanceDue) - toNumber(a.balanceDue))
        .slice(0, 6)
        .map((tx) => ({ ...tx, _dueDate: toDate(tx.dueDate) }))
    },
    payables: {
      outstanding: payablesOutstanding,
      overdue: overduePayables,
      openCount: openBills.length,
      overdueCount: overduePayablesList.length,
      top: overduePayablesList
        .sort((a, b) => toNumber(b.balanceDue) - toNumber(a.balanceDue))
        .slice(0, 6)
        .map((tx) => ({ ...tx, _dueDate: toDate(tx.dueDate) }))
    },
    tax: {
      collected: taxCollected,
      paid: taxPaid,
      liability: round2(taxCollected - taxPaid)
    },
    cash: {
      onHand: cashBalance,
      in: current.cashIn,
      out: current.cashOut,
      net: round2(current.cashIn - current.cashOut)
    },
    balanceSheet,
    revenueByCategory,
    expensesByCategory,
    monthlyNet,
    recentTransactions
  };
};
